import {
  DownloadOutlined,
  CheckOutlined,
  CloseOutlined,
  CaretUpOutlined,
  CaretDownOutlined,
  MinusOutlined
} from '@ant-design/icons'
import cx from 'classnames'

import { API_URL } from '../../../../API'
import { reportTypeNameMapper } from '../constants'
import { formatValue } from '../functions'

interface ReportCommon {
  voided: number
  payments: number
  temp_s3: string
  totals: {
    ACH: {
      voided: number
      payments: number
    }
    Checks: {
      voided: number
      payments: number
    }
    'Electronic Payments': {
      voided: number
      payments: number
    }
  }
}

interface ReportNetwork {
  voided: number
  payments: number
  temp_s3: string
  totals: {
    'Network Detail': {
      voided: number
      payments: number
    }
    'Network Summary': {
      voided: number
      payments: number
    }
  }
}

export interface ReportTotals {
  detail: ReportCommon
  summary: ReportCommon
  'network-detail': ReportNetwork
  'network-summary': ReportNetwork
}

function round(value: number): number {
  return Math.round(value * 100) / 100
}

interface CheckRegistersProps {
  reportTotals: ReportTotals
  warning?: string | null
  hasData?: boolean
}

export function CheckRegisters({
  reportTotals,
  warning,
  hasData
}: CheckRegistersProps): JSX.Element {
  const token = window.localStorage.getItem('token')
  const rows: Array<[keyof ReportTotals, keyof ReportTotals]> = [
    ['summary', 'detail'],
    ['network-summary', 'network-detail']
  ]

  return (
    <div className='w-full max-w-[640px] self-start box-border text-left py-4 px-3 pr-12 mb-4 border border-stone-200 rounded-xl ml-5'>
      <div className='flex flex-row gap-4'>
        <div
          className={cx(
            'w-6 h-6 rounded-full flex items-center justify-center text-[10px]',
            !hasData
              ? 'bg-gray-200 text-gray-400'
              : warning
                ? 'bg-[#FCCCC3] text-red-600'
                : 'bg-[#C3FCC7] text-green-600'
          )}
        >
          {!hasData ? <MinusOutlined /> : warning ? <CloseOutlined /> : <CheckOutlined />}
        </div>

        <h1 className='text-base font-bold'>Check Registers</h1>
      </div>
      <div className='px-4 flex flex-col gap-2 mt-4'>
        {rows.map(([leftKey, rightKey]) => {
          return (
            <div key={`${leftKey}-${rightKey}`} className='grid grid-cols-2 gap-12'>
              {([leftKey, rightKey] as const).map(reportKey => {
                const report = reportTotals?.[reportKey]
                if (!report) {
                  return null
                }

                const headerTotal = round(report.payments + Math.abs(report.voided))

                const entries = Object.entries(report.totals) as Array<
                  [string, { payments: number; voided: number }]
                >

                return (
                  <div key={reportKey} className='rounded-lg flex flex-col pl-6'>
                    {/* ───────────────── Header ───────────────── */}
                    <div className='flex items-center gap-4 justify-between mb-4 relative'>
                      <h2 className='text-sm font-semibold'>{reportTypeNameMapper[reportKey]}</h2>
                      <div className='flex items-center space-x-2'>
                        <span className='text-base font-semibold'>{formatValue(headerTotal)}</span>
                        <a
                          href={`${API_URL}/files/from-path/${encodeURIComponent(report.temp_s3)}?token=${encodeURIComponent(token ?? '')}`}
                          type='button'
                          className='absolute -right-10 w-8 h-8 flex justify-center items-center border-solid border-gray-300 rounded hover:bg-gray-50'
                        >
                          <DownloadOutlined width={15} height={15} className='text-blue-500' />
                        </a>
                      </div>
                    </div>

                    <div className='flex flex-col space-y-3'>
                      {entries.map(([itemKey, data]) => {
                        let expectedPayments: number | undefined
                        switch (reportKey) {
                          case 'detail': {
                            expectedPayments =
                              reportTotals.summary.totals[itemKey as keyof ReportCommon['totals']]
                                .payments
                            break
                          }
                          case 'network-detail': {
                            expectedPayments =
                              reportTotals['network-detail'].totals['Network Detail'].payments
                            break
                          }
                          case 'network-summary': {
                            expectedPayments =
                              reportTotals['network-summary'].totals['Network Summary'].payments
                            break
                          }
                        }

                        const isMismatch =
                          expectedPayments !== undefined && data.payments !== expectedPayments

                        const diff = data.payments - (expectedPayments ?? 0)

                        const paymentRow = (
                          <div className='pl-2 flex justify-between items-center'>
                            <span
                              className={`text-smx font-semibold ${
                                isMismatch ? 'text-red-500' : 'text-zinc-600'
                              }`}
                            >
                              {itemKey}
                            </span>
                            <div className='flex flex-col items-end'>
                              <span
                                className={`text-base font-medium ${
                                  isMismatch ? 'text-red-500' : 'text-gray-900'
                                }`}
                              >
                                {formatValue(data.payments)}
                              </span>
                              {isMismatch && (
                                <div className='text-red-600 flex flex-row items-center gap-1'>
                                  {diff > 0 ? (
                                    <CaretUpOutlined className='text-red-600' />
                                  ) : (
                                    <CaretDownOutlined className='text-red-600' />
                                  )}
                                  <span>{formatValue(diff)}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        )

                        const voidedRow = (
                          <div
                            key={`${reportKey}-${itemKey}-voided`}
                            className='pl-2 flex justify-between items-center'
                          >
                            <span className='text-smx font-semibold text-zinc-600'>
                              {itemKey} Voided
                            </span>
                            <span className='text-base font-medium text-gray-900'>
                              {formatValue(Math.abs(data.voided))}
                            </span>
                          </div>
                        )

                        return (
                          <div key={itemKey} className='space-y-1'>
                            {paymentRow}
                            {voidedRow}
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )
              })}
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default CheckRegisters
